import React, { useState, useEffect } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { deliveryMethodService, CreateDeliveryMethodRequest, LayoutLookupDto } from '../../../../../services/deliveryMethodService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface DeliveryMethodCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const DeliveryMethodCreateModal: React.FC<DeliveryMethodCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState<CreateDeliveryMethodRequest>({
        companyNo: user?.companyNo || '001',
        deliveryMethodNo: 1,
        description: null,
        externalID: null,
        consignmentSWDefProductNo: null,
        consignmentSWAdditionalCode: null,
        shippingLabelsYesNo: false,
        shippingLabelsLayoutNo: null,
        labelExportFolder: null,
        labelExportFilename: null,
        packageSlipERPYesNo: false,
        shippingLabelsERPYesNo: false,
        consignmentNoteERPYesNo: false,
        singleParcelDelivery: false,
        consignmentSWDefProductNoIsNShiftCSID: false,
        autoBookYesNo: false,
        requirePackageDetailsYesNo: false,
        pickCompletionWarningYesNo: false,
        pickCompletionWarningText: null
    });
    const [layouts, setLayouts] = useState<LayoutLookupDto[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        deliveryMethodService.getLayouts().then(setLayouts).catch(() => {});
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.deliveryMethodNo || formData.deliveryMethodNo < 1) {
            setError(t('manager.modules.adminDelivery.deliveryMethod.errors.deliveryMethodNoRequired', {}, 'Leveringsmåte nr. er obligatorisk'));
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await deliveryMethodService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminDelivery.common.errors.createFailed', {}, 'Feil ved opprettelse'));
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof CreateDeliveryMethodRequest>(key: K, value: CreateDeliveryMethodRequest[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminDelivery.deliveryMethod.create.title', {}, 'Ny leveringsmåte')}</h2>
                            <p className="text-sm text-green-100">{t('manager.modules.adminDelivery.deliveryMethod.create.subtitle', {}, 'Opprett en ny leveringsmåte')}</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">{t('manager.modules.adminDelivery.deliveryMethod.create.success', {}, 'Leveringsmåte opprettet!')}</p>
                                </div>
                            </div>
                        )}

                        {/* Grunndata */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminDelivery.deliveryMethod.sections.basicData', {}, 'Grunndata')}</legend>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t('manager.modules.adminDelivery.deliveryMethod.fields.deliveryMethodNo', {}, 'Leveringsmåte nr.')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.deliveryMethodNo}
                                        onChange={(e) => updateField('deliveryMethodNo', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                        min={1}
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.fields.description', {}, 'Beskrivelse')}</label>
                                    <input
                                        type="text"
                                        value={formData.description || ''}
                                        onChange={(e) => updateField('description', e.target.value || null)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        maxLength={100}
                                        placeholder={t('manager.modules.adminDelivery.deliveryMethod.placeholders.description', {}, 'F.eks. Pakkepost, Stykkgods...')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.deliveryMethod.fields.externalID', {}, 'Transportør ID')}</label>
                                    <input
                                        type="text"
                                        value={formData.externalID || ''}
                                        onChange={(e) => updateField('externalID', e.target.value || null)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        maxLength={50}
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* TA-system */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminDelivery.deliveryMethod.sections.taSystem', {}, 'TA-system')}</legend>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.deliveryMethod.fields.defaultProductNo', {}, 'Standard produktnr.')}</label>
                                    <input
                                        type="text"
                                        value={formData.consignmentSWDefProductNo || ''}
                                        onChange={(e) => updateField('consignmentSWDefProductNo', e.target.value || null)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        maxLength={50}
                                        placeholder={t('manager.modules.adminDelivery.deliveryMethod.placeholders.defaultProductNo', {}, 'F.eks. 5100')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.deliveryMethod.fields.additionalCode', {}, 'Tilleggskode')}</label>
                                    <input
                                        type="text"
                                        value={formData.consignmentSWAdditionalCode || ''}
                                        onChange={(e) => updateField('consignmentSWAdditionalCode', e.target.value || null)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        maxLength={100}
                                    />
                                </div>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.consignmentSWDefProductNoIsNShiftCSID}
                                        onChange={(e) => updateField('consignmentSWDefProductNoIsNShiftCSID', e.target.checked)}
                                        className="w-4 h-4 mt-0.5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {t('manager.modules.adminDelivery.deliveryMethod.fields.nshiftProdCSID', {}, 'NShift: intern/custom transportør (bruk ProdCSID)')}
                                        <span className="block text-xs text-gray-500">{t('manager.modules.adminDelivery.deliveryMethod.help.nshiftProdCSID', {}, 'Send produktnr. som ProdCSID i stedet for ProdConceptID. Kun for NShift-transportører uten katalog-produkt.')}</span>
                                    </span>
                                </label>
                            </div>
                        </fieldset>

                        {/* Etikett / Eksport */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminDelivery.deliveryMethod.sections.labelExport', {}, 'Etikett og eksport')}</legend>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.deliveryMethod.fields.labelLayout', {}, 'Etikettlayout')}</label>
                                    {layouts.length > 0 ? (
                                        <select
                                            value={formData.shippingLabelsLayoutNo ?? ''}
                                            onChange={(e) => updateField('shippingLabelsLayoutNo', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                        >
                                            <option value="">{t('manager.modules.adminDelivery.deliveryMethod.options.none', {}, '-- Ingen --')}</option>
                                            {layouts.map(l => (
                                                <option key={l.layoutNo} value={l.layoutNo}>
                                                    {l.layoutNo} - {l.description || ''}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="number"
                                            value={formData.shippingLabelsLayoutNo ?? ''}
                                            onChange={(e) => updateField('shippingLabelsLayoutNo', e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                            placeholder={t('manager.modules.adminDelivery.deliveryMethod.placeholders.layoutNo', {}, 'Layout nr.')}
                                        />
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.deliveryMethod.fields.labelExportFolder', {}, 'Eksportkatalog')}</label>
                                    <input
                                        type="text"
                                        value={formData.labelExportFolder || ''}
                                        onChange={(e) => updateField('labelExportFolder', e.target.value || null)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        maxLength={500}
                                        placeholder={t('manager.modules.adminDelivery.deliveryMethod.placeholders.labelExportFolder', {}, 'F.eks. C:\\Labels\\Export')}
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Flagg */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminDelivery.deliveryMethod.sections.settings', {}, 'Innstillinger')}</legend>
                            <div className="space-y-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.shippingLabelsYesNo}
                                        onChange={(e) => updateField('shippingLabelsYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.shippingLabels', {}, 'Fraktetiketter')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.singleParcelDelivery}
                                        onChange={(e) => updateField('singleParcelDelivery', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.singleParcelDelivery', {}, 'Enkeltkolli-levering')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.autoBookYesNo}
                                        onChange={(e) => updateField('autoBookYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.autoBook', {}, 'Auto-book (Apport)')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.requirePackageDetailsYesNo}
                                        onChange={(e) => updateField('requirePackageDetailsYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.requirePackageDetails', {}, 'Krev kollidetaljer ved avslutning')}</span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.pickCompletionWarningYesNo}
                                        onChange={(e) => updateField('pickCompletionWarningYesNo', e.target.checked)}
                                        className="w-4 h-4 mt-0.5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {t('manager.modules.adminDelivery.deliveryMethod.fields.pickCompletionWarning', {}, 'Varsle ved avslutt plukk')}
                                        <span className="block text-xs text-gray-500">{t('manager.modules.adminDelivery.deliveryMethod.help.pickCompletionWarning', {}, 'Operatøren får en popup som må bekreftes med OK når plukket avsluttes. F.eks. for ekspress-sendinger.')}</span>
                                    </span>
                                </label>
                                {formData.pickCompletionWarningYesNo && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.deliveryMethod.fields.pickCompletionWarningText', {}, 'Varslingstekst')}</label>
                                        <input
                                            type="text"
                                            value={formData.pickCompletionWarningText || ''}
                                            onChange={(e) => updateField('pickCompletionWarningText', e.target.value || null)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                            maxLength={500}
                                            placeholder={t('manager.modules.adminDelivery.deliveryMethod.placeholders.pickCompletionWarningText', {}, 'F.eks. OBS: Ekspress sending')}
                                        />
                                    </div>
                                )}
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.packageSlipERPYesNo}
                                        onChange={(e) => updateField('packageSlipERPYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.packageSlipFromERP', {}, 'Pakkseddel fra ERP')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.shippingLabelsERPYesNo}
                                        onChange={(e) => updateField('shippingLabelsERPYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.shippingLabelsFromERP', {}, 'Fraktetiketter fra ERP')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.consignmentNoteERPYesNo}
                                        onChange={(e) => updateField('consignmentNoteERPYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminDelivery.deliveryMethod.fields.consignmentNoteFromERP', {}, 'Fraktbrev fra ERP')}</span>
                                </label>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminDelivery.common.creating', {}, 'Oppretter...')}
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        {t('common.buttons.create', {}, 'Opprett')}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
